import type { TagValue } from '../services/sentry/types';

/** `origin + pathname` of `url` (relative to the page); query and hash dropped as they may carry tokens. */
export function toSafeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin + parsed.pathname;
  } catch (e) {
    return 'invalid_url';
  }
}

/**
 * Error class name only (never the message, which may carry user data). Duck-typed `name` too,
 * so errors from another realm and error-like objects keep their name.
 */
export function getErrorName(error: unknown): string {
  if (typeof error !== 'object' || error === null) return typeof error;
  try {
    const { name } = error as { name?: unknown };
    if (error instanceof Error || (typeof name === 'string' && name !== '')) {
      return String(name);
    }
  } catch (e) {
    // Proxy or throwing getter
  }
  return typeof error;
}

/** Primitive `code` of an error (e.g. `GatewayError`'s numeric code), if any. */
export function getErrorCode(error: unknown): string | number | undefined {
  try {
    const code = (error as { code?: unknown } | null)?.code;
    return typeof code === 'string' || typeof code === 'number'
      ? code
      : undefined;
  } catch (e) {
    // Proxy or throwing getter
    return undefined;
  }
}

/** A method call's arguments as one flat record: primitive fields of object args, primitive args by position. */
export function flattenArgs(args: unknown[]): Record<string, TagValue> {
  try {
    return Object.fromEntries(
      args
        .flatMap((arg, i) =>
          Object.entries(arg && typeof arg === 'object' ? arg : { [i]: arg }),
        )
        .filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v)),
    ) as Record<string, TagValue>;
  } catch {
    return {};
  }
}

/** Milliseconds elapsed since `start` (a `Date.now()` value). */
export function elapsedMs(start: number): number {
  return Date.now() - start;
}

/** Whether `error` marks itself as expected (`capture: false`, e.g. a `GatewayError`). */
export function isExpectedError(error: unknown): boolean {
  try {
    return (error as { capture?: unknown } | null)?.capture === false;
  } catch (e) {
    // Proxy or throwing getter
    return false;
  }
}
